import "devextreme/dist/css/dx.fluent.blue.light.compact.css";
import { useEffect } from "react";
import { SquareArrowOutUpRight } from "lucide-react";
import DataGrid, {
  Column,
  FilterRow,
  MasterDetail,
  Pager,
  Paging,
  SearchPanel,
} from "devextreme-react/data-grid";
import type { MailGridRowDto } from "../../../shared/types";
import { useMailGrid, useMessage } from "../../hooks/useApi";
import { ErrorState } from "../common/ErrorState";
import { bodyDoc } from "./mailContent";

/** Expanded row: the full email, fetched on demand, in a sandboxed iframe. */
function MailDetail({ id }: { id: string }) {
  const { data, error, isLoading } = useMessage(0, id);
  if (isLoading) return <p className="p-3 text-[12px] text-ink-muted">Loading email…</p>;
  if (error) return <p className="p-3 text-[12px] text-red-700">{(error as Error).message}</p>;
  if (!data) return null;
  return (
    <div className="bg-white p-2">
      <p className="px-1 pb-1.5 text-[12px] text-ink-muted">
        {data.from?.name ?? data.from?.address ?? "Unknown sender"}
        {data.from?.address ? ` <${data.from.address}>` : ""} ·{" "}
        {new Date(data.receivedDateTime).toLocaleString()}
      </p>
      {/* Sandboxed like ReadingPane: no scripts, remote content blocked. */}
      <iframe
        sandbox=""
        srcDoc={bodyDoc(data.bodyHtml, false)}
        title="Email content"
        className="h-96 w-full rounded border border-stroke bg-white"
      />
    </div>
  );
}

/** "Search emails": every stored mail (minus Trash/Junk) in a filterable grid. */
export function SearchTab({ onOpenMessage }: { onOpenMessage: (id: string) => void }) {
  const { data, error, isLoading } = useMailGrid(true);

  // Dismiss the DevExtreme trial license notice once the grid renders.
  useEffect(() => {
    const parent = document.querySelector("dx-license");
    if (parent) {
      const secondDiv = parent.querySelectorAll("div")[1];
      if (secondDiv) secondDiv.click();
    }
  }, [data]);

  if (isLoading) {
    return <p className="p-4 text-[13px] text-ink-muted">Loading emails…</p>;
  }
  if (error) {
    return (
      <div className="p-4">
        <ErrorState error={error as Error} />
      </div>
    );
  }

  return (
    <div className="compact-mail-grid min-h-0 w-full flex-1 p-2">
      <DataGrid
        dataSource={data ?? []}
        keyExpr="id"
        height="100%"
        showBorders
        columnAutoWidth
        allowColumnResizing
        wordWrapEnabled={false}
      >
        <FilterRow visible />
        <SearchPanel visible width={240} />
        <Paging defaultPageSize={100} />
        <Pager visible showPageSizeSelector allowedPageSizes={[25, 50, 100]} showInfo />
        <Column
          caption=""
          width={40}
          allowFiltering={false}
          allowSorting={false}
          allowResizing={false}
          cellRender={({ data: row }: { data: MailGridRowDto }) => (
            <button
              onClick={() => onOpenMessage(row.id)}
              title="Open in Email tab"
              aria-label="Open in Email tab"
              className="grid size-5 place-items-center rounded text-ink-muted hover:bg-black/10 hover:text-accent"
            >
              <SquareArrowOutUpRight size={13} />
            </button>
          )}
        />
        <Column
          dataField="receivedDateTime"
          caption="Date"
          dataType="datetime"
          defaultSortOrder="desc"
          sortIndex={0}
          width={150}
        />
        <Column dataField="from" caption="From" width={170} />
        <Column dataField="account" caption="Account" width={190} />
        <Column dataField="subject" caption="Subject" />
        <Column dataField="preview" caption="Content" />
        <MasterDetail enabled render={({ data: row }) => <MailDetail id={row.id} />} />
      </DataGrid>
    </div>
  );
}
